// Phase 10B "AutoMind 2050" — Login.
//
// Login is rendered OUTSIDE AppShell, so we mount AmbientBackground
// directly here so the gold orbs stay visible behind the auth card.

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

import { useAuth } from '../auth/AuthContext';
import AmbientBackground from '../components/ui/ambient-background';

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email.trim().toLowerCase(), password, rememberMe);
      toast.success('Login successful');
    } catch (err) {
      const msg = err.message || 'Login failed. Please check your credentials.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    'w-full bg-white/60 dark:bg-ink-900/40 border border-ink-200/60 dark:border-ink-800/60 ' +
    'rounded-md px-3 py-2 text-sm focus-2050';
  const labelClass = 'block text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1.5';

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 bg-ink-50 dark:bg-ink-950 overflow-hidden">
      <AmbientBackground />

      <div className="card-2050 w-full max-w-md p-8 relative z-10" data-testid="login-card">
        <div className="space-y-1.5 mb-6 text-center">
          <p className="text-[11px] uppercase tracking-[0.18em] text-ink-500">
            Dealership AI
          </p>
          <h1 className="font-display text-[26px] leading-tight font-semibold tracking-tight">
            Sign in to AutoMind
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              className="pill-2050 pill-danger w-full justify-start text-xs px-3 py-2"
              data-testid="login-error-text"
              role="alert"
            >
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className={labelClass}>Email</label>
            <input
              id="email"
              type="email"
              placeholder="you@dealership.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              required
              autoComplete="email"
              data-testid="login-email-input"
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="password" className={labelClass}>Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputClass} pr-10`}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900 dark:hover:text-ink-50 p-1"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-ink-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              data-testid="login-remember-checkbox"
              disabled={isLoading}
              className="accent-gold"
            />
            Remember me for 14 days
          </label>

          <button
            type="submit"
            className="btn-2050 btn-primary w-full justify-center h-10"
            disabled={isLoading || !email || !password}
            data-testid="login-submit-button"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signing in
              </span>
            ) : (
              'Sign in'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
